import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Stage, Layer, Image, Rect } from 'react-konva';
import { Portal } from 'react-konva-utils';
import { toast } from 'react-toastify';
import useImage from 'use-image';
import queryString from 'query-string';

import {
  MainContentContainer,
  ZoomToolContainer,
  BottomUsersNavContainer2,
} from './styles';
import LaunchPadLayout from '../../../Layouts/LaunchPadLayout';
import { ModalFooter } from '../../../Components/CommonStyles';

import BottomUsersNav from '../../../Components/BottomUsersNav';
import ZoomTool from '../../../Components/ZoomTool';
import BottomInstanceNav from '../../../Components/BottomInstanceNav';
import RobotCanvasItem from '../../../Components/Konva/RobotCanvasItem';
import InstanceLauncherPopup from 'Components/InstanceLauncherPopup'

import { apiGetAllShootInstancesLiveStatusRequest } from '../../../Redux/actions/gear';
import { apiGetOnlineMembers } from 'Redux/actions/user_information';
import useRobotLiveStatus from 'hooks/useRobotLiveStatus';
import { WebsocketClients } from 'Config/websocketConstants';

const Main = ({ state, project_id }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const ref = useRef();
  const stageRef = React.useRef();
  const firstInstanceStatusCall = useRef(false);
  const [image] = useImage(state.shoot?.background_image);

  // useSelector
  const auth = useSelector(state => state.auth);
  const sideNavState = useSelector(state => state.sidebar);
  const gear = useSelector(state => state.gear);
  const userInformation = useSelector(state => state.userInformation);

  // Other
  const params = queryString.parse(window.location.search);
  const INTERVAL_MS = 30000;

  // useState
  const [scale, setScale] = useState();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectedRobotId, setSelectedRobotId] = useState(null);
  const [isZooming, setIsZooming] = useState(false);
  const shootInstances = gear?.shootInstances;
  const [shootRobots, setShootRobots] = useState({});
  const [airIdToRobotId, setAirIdToRobotId] = useState({});
  const robotLiveStatus = useRobotLiveStatus(WebsocketClients.LAUNCHPAD, airIdToRobotId);
  const [selectedInstance, setSelectedInstance] = useState({});
  const [selectedInstanceNickName, setSelectedInstanceNickName] = useState('');
  const [showInstanceLaunchModal, setShowInstanceLaunchModal] = useState(false);

  const links = [
    {
      title: 'Launch Pad',
      href: `/project/launchpad/${project_id}`,
    },
  ];
  const members = sideNavState.selectedProject?.members;
  const ownerFirstName = sideNavState.selectedProject?.owner?.first_name ?? 'n/a';
  const ownerLastName = sideNavState.selectedProject?.owner?.last_name ?? 'n/a';
  let ownerName;
  if (ownerFirstName !== 'n/a' && ownerLastName !== 'n/a') {
    ownerName = ownerFirstName + ' ' + ownerLastName;
  } else if (ownerFirstName !== 'n/a') {
    ownerName = ownerFirstName;
  } else if (ownerLastName !== 'n/a') {
    ownerName = ownerLastName;
  } else {
    ownerName = 'n/a';
  }
  const ownerEmail = sideNavState.selectedProject?.owner?.email ?? 'n/a';
  const projectName = sideNavState.selectedProject?.name ?? '';
  const orgName = sideNavState.selectedProject?.organisation?.name ?? '';

  const curUserId = userInformation?.data?.user ?? 'n/a';
  const curUserFirstName = userInformation?.data?.first_name ?? 'n/a';
  const curUserLastName = userInformation?.data?.last_name ?? 'n/a';
  let curUserName;
  if (curUserFirstName !== 'n/a' && curUserLastName !== 'n/a') {
    curUserName = curUserFirstName + ' ' + curUserLastName;
  } else if (curUserFirstName !== 'n/a') {
    curUserName = curUserFirstName;
  } else if (curUserLastName !== 'n/a') {
    curUserName = curUserLastName;
  } else {
    curUserName = 'n/a';
  }
  const curUserEmail = userInformation?.data?.email ?? 'n/a';
  var lastCenter = null;
  var lastDist = 0;

  const setStageDims = () => {
    if (stageRef) {
      if (image) {
        const stageCenter = {
          x: ref.current?.offsetWidth / 2,
          y: ref.current?.offsetHeight / 2,
        };
        const backgroundImageCenter = {
          x: (image.width * scale) / 2,
          y: (image.height * scale) / 2,
        };

        const centerPoint = {
          x: stageCenter.x - backgroundImageCenter.x,
          y: stageCenter.y - backgroundImageCenter.y,
        };

        setPosition(centerPoint);
      }

      stageRef.current?.width(ref.current.offsetWidth);
      stageRef.current?.height(ref.current.offsetHeight);
    }
  };

  const handleWheel = event => {
    event.evt.preventDefault();
    const currentStageRef = stageRef.current;

    if (currentStageRef) {
      const stage = currentStageRef.getStage();

      if (event.evt.ctrlKey) {
        const oldScale = stage.scaleX();

        const mousePointTo = {
          x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
          y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale,
        };

        const unboundedNewScale = oldScale - event.evt.deltaY * 0.01;
        let newScale = unboundedNewScale;
        if (unboundedNewScale < 0.1) {
          newScale = 0.1;
        } else if (unboundedNewScale > 10.0) {
          newScale = 10.0;
        }

        const newPosition = {
          x:
            -(mousePointTo.x - stage.getPointerPosition().x / newScale) *
            newScale,
          y:
            -(mousePointTo.y - stage.getPointerPosition().y / newScale) *
            newScale,
        };

        setScale(newScale);
        setPosition(newPosition);
      } else {
        const dragDistanceScale = 0.75;
        const newPosition = {
          x: position.x - dragDistanceScale * event.evt.deltaX,
          y: position.y - dragDistanceScale * event.evt.deltaY,
        };

        setPosition(newPosition);
      }
    }
  };

  const handleZoomIn = (isScale = true) => {
    const currentStageRef = stageRef.current;

    if (currentStageRef) {
      const stage = currentStageRef.getStage();
      const oldScale = stage.scaleX();
      const mousePointTo = {
        x: ref.current.offsetWidth / 2 / oldScale - stage.x() / oldScale,
        y: ref.current.offsetHeight / 2 / oldScale - stage.y() / oldScale,
      };

      const unboundedNewScale = oldScale;
      let newScale = unboundedNewScale;
      if (isScale)
        newScale = Math.min(10.0, Math.ceil(oldScale * 1.1 * 10) / 10);

      const newPosition = {
        x:
          -(mousePointTo.x - ref.current.offsetWidth / 2 / newScale) * newScale,
        y:
          -(mousePointTo.y - ref.current.offsetHeight / 2 / newScale) *
          newScale,
      };

      setScale(newScale);
      setPosition(newPosition);
    }
  };

  const handleZoomOut = (isScale = true) => {
    const currentStageRef = stageRef.current;

    if (currentStageRef) {
      const stage = currentStageRef.getStage();
      const oldScale = stage.scaleX();
      const mousePointTo = {
        x: ref.current.offsetWidth / 2 / oldScale - stage.x() / oldScale,
        y: ref.current.offsetHeight / 2 / oldScale - stage.y() / oldScale,
      };

      const unboundedNewScale = oldScale;
      let newScale = unboundedNewScale;
      if (isScale)
        newScale = Math.max(0.1, Math.floor(oldScale * 0.9 * 10) / 10);

      const newPosition = {
        x:
          -(mousePointTo.x - ref.current.offsetWidth / 2 / newScale) * newScale,
        y:
          -(mousePointTo.y - ref.current.offsetHeight / 2 / newScale) *
          newScale,
      };

      setScale(newScale);
      setPosition(newPosition);
    }
    // setStageDims();
    // setScale(prevValue => Math.max(0.1, Math.floor(prevValue * 0.9 * 10) / 10));
    // // setIsCanvasDirty(true);
    // setStageDims();
  };

  const handleCentered = () => {
    setStageDims();
  };

  const handleFocusOut = () => {
    setSelectedRobotId(null);
    history.push(window.location.pathname);
  };

  const getDistance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  const getCenter = (p1, p2) => {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
  };

  const handleMultiTouch = e => {
    e.evt.preventDefault();

    var touch1 = e.evt.touches[0];
    var touch2 = e.evt.touches[1];
    const stage = e.target.getStage();

    if (touch1 && touch2) {
      setIsZooming(true);

      var p1 = {
        x: touch1.clientX,
        y: touch1.clientY,
      };
      var p2 = {
        x: touch2.clientX,
        y: touch2.clientY,
      };

      if (!lastCenter) {
        lastCenter = getCenter(p1, p2);
        return;
      }
      let newCenter = getCenter(p1, p2);

      let dist = getDistance(p1, p2);

      if (!lastDist) {
        lastDist = dist;
      }

      // local coordinates of center point
      let pointTo = {
        x: (newCenter.x - stage.x()) / stage.scaleX(),
        y: (newCenter.y - stage.y()) / stage.scaleX(),
      };

      let scale = stage.scaleX() * (dist / lastDist);
      // setScale(scale);
      stage.scaleX(scale);
      stage.scaleY(scale);

      // calculate new position of the stage
      let dx = newCenter.x - lastCenter.x;
      let dy = newCenter.y - lastCenter.y;

      let newPos = {
        x: newCenter.x - pointTo.x * scale + dx,
        y: newCenter.y - pointTo.y * scale + dy,
      };

      stage.position(newPos);
      stage.batchDraw();

      lastDist = dist;
      lastCenter = newCenter;
    }
  };

  const multiTouchEnd = () => {
    lastCenter = null;
    lastDist = 0;
    setIsZooming(false);
  };

  const handleDragStart = e => {
    const stage = e.target.getStage();

    if (isZooming) {
      stage.stopDrag();
    }
    handleZoomIn(false);
    handleZoomOut(false);
  };

  const currentUserRole = () => {
    return userInformation?.data?.organisation_data?.find(item => item.organisation.id === userInformation?.selectedOrganisation)?.role;
  };

  const onDblClick = (robotId, gearData, type='ROBOT') => {
    if (type === 'instance') {
      setSelectedRobotId(null);
      let instanceArr = gear.shootInstanceLiveStatuses.filter(instance => instance.air_id === gearData.air_id);
      if (instanceArr.length) {
        let selectedInstance = instanceArr[0];
        if (selectedInstance.status === "Running") {
          if (gearData?.instance_type === "AUTOPILOT") {
            window.open(`http://${selectedInstance?.public_ip}:3000`);
          } else {
            window.open(`dcv://${selectedInstance?.public_ip}`);
          }
        } else {
          toast.error("This instance's not started yet or still pending. Single click to check the instance status");
        }
      } else {
        toast.error("This instance's AIR Id is not properly configured. Contact AIR admin to solve the issue.");
      }
      return;
    } else {
      setSelectedRobotId(robotId);
      const ip = (gearData?.public_ip !== "" && gearData?.public_ip !== undefined)
                ? gearData.public_ip
                : (gearData?.lan_ip !== "" && gearData?.lan_ip !== undefined)
                  ? gearData.lan_ip
                  : "";
      const robotPlaceholder = gearData?.robot_placeholder;
      const robotName = 'Robot' + robotPlaceholder + '-' + gearData?.shoot_robot_nick_name.trim().replace(/\s/g, "-");

      if (ip === '') {
        toast.error('The ip address of this robot is not set, please set it up in the Shoot Setup page.');
        return;
      } else if (robotPlaceholder === '') {
        toast.error('The placeholder of this robot is not set, please set it up in the Shoot Setup page.');
        return;
      }

      const internalRecordFormat = (gearData?.internal_record_format || '')?.split('/');
      const recordingResolution = internalRecordFormat[0] === '1080'
                                    ? '1920x1080'
                                    : internalRecordFormat[0] === '4k'
                                      ? '4K'
                                      : '';
      const recordingFPS = internalRecordFormat[1] || '';
      const encoder = internalRecordFormat[2] || '';

      const [streamingIP, streamingPort] = (gearData?.stream_destination_ip || '').split(":");
      const streamings = gearData?.external_stream_format?.split('/');
      const streamingResolution = streamings[0] || '';
      const streamingFPS = streamings[1] || '';
      const streamingCodec = streamings[2] || '';

      const url = `airapp://ip=${ip}&cameraInstance=${robotPlaceholder}&cameraName=${robotName}&recordingResolution=${recordingResolution}&recordingFPS=${recordingFPS}&encoder=${encoder}&streamingIP=${streamingIP}&streamingPort=${streamingPort}&streamingResolution=${streamingResolution}&streamingFPS=${streamingFPS}&streamingCodec=${streamingCodec}`;
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('target', '_blank');
      a.click();
    }
  };

  const getRobotOperatorData = (robotId) => {
    let operatorData = []; // robot operators with online status
    let connectedOperators = shootRobots[robotId].operators;
    let onlineMembers = userInformation?.onlineMembers
    if (connectedOperators.length) {
      if (members) {
        connectedOperators.forEach(cMember => {
          members.forEach(member => {
            if (member.user.toString() === cMember.toString()) {
              member['isOnline'] = false;
              const getStatus = onlineMembers?.find(item => item[cMember]);
              if (getStatus)
                member['isOnline'] = getStatus[Object.keys(getStatus)[0]];
              operatorData.push(member);
              return;
            }
          });
        });
        operatorData.sort((a, b) => (a.isOnline > b.isOnline ? 1 : -1));
      }
    }
    return operatorData;
  }

  const convertRobotStates = (inputStates) => {
    let shootRobotObjs = {};
    let tempAirIdToRobotId = {};
    inputStates.forEach((state) => {
      shootRobotObjs[state.id] = state;
      tempAirIdToRobotId[state.air_id] = state.robot_id;
    });
    return [shootRobotObjs, tempAirIdToRobotId];
  };

  const onClickInstance = (instanceAirId, instanceNickName) => {
    if (gear.shootInstanceLiveStatuses?.length) {
      let instanceArr = gear.shootInstanceLiveStatuses.filter(instance => instance.air_id === instanceAirId);
      if (instanceArr.length) {
        setSelectedInstance(instanceArr[0]);
        setSelectedInstanceNickName(instanceNickName);
        setShowInstanceLaunchModal(true);
      } else {
        toast.error("This instance's AIR Id is not properly configured. Contact AIR admin to solve the issue.");
      }
      return;
    } else {
      toast.info("Fetching instance's status, please wait.");
      return;
    }
  };

  const getAllShootInstancesLiveStatus = () => {
    if (shootInstances) {
      let instancesArr = [];
      for (const shootInstance of shootInstances) {
        if (!(shootInstance.order > 0 && shootInstance.order < 7)) {
          continue
        }
        let regionName = shootInstance?.instance_regioin;
        let instanceAwsId = shootInstance?.air_id;
        if (!regionName || !instanceAwsId) {
          continue; // Make sure the region Name and the instance's AWS ID (AIR ID) is there
        } else {
          instancesArr.push([regionName, instanceAwsId])
        }
      }
      dispatch(apiGetAllShootInstancesLiveStatusRequest(auth.token, instancesArr));
    }
    return;
  }

  useEffect(() => {
    let onlineMembersTimer;
    if (project_id) {
      dispatch(apiGetOnlineMembers(project_id, auth.token));
      onlineMembersTimer = setInterval(
        () => dispatch(apiGetOnlineMembers(project_id, auth.token)),
        1000 * 30,
      );
    }

    return () => {
      onlineMembersTimer && clearInterval(onlineMembersTimer);
    };
  }, [project_id]);

  useEffect(() => {
    if (state?.shoot?.scale) {
      setScale(state.shoot?.scale);
    }
  }, [state.shoot]);

  useEffect(() => {
    window.addEventListener('resize', setStageDims);

    dispatch(apiGetOnlineMembers(project_id, auth.token));
    return () => {
      window.removeEventListener('resize', setStageDims);
    };
  }, [state]);

  useEffect(() => {
    setStageDims();
  }, [image, ref.current]);

  useEffect(() => {
    let [shootRobotObjs, tempAirIdToRobotId] = convertRobotStates(gear.shootRobots);
    setShootRobots(shootRobotObjs);
    setAirIdToRobotId(tempAirIdToRobotId);
  }, [gear.shootRobots]);

  useEffect(() => {
    // Only call the getAllShootInstancesLiveStatus if shootInstances is not empty and InstanceLiveStatusInterval is not initialized
    if (!shootInstances?.length && firstInstanceStatusCall.current === false) {
      return;
    }
    firstInstanceStatusCall.current = true;
    getAllShootInstancesLiveStatus();
    const instanceLiveStatusIntervalId = setInterval(() => {
      getAllShootInstancesLiveStatus();
    }, INTERVAL_MS);
    return () => clearInterval(instanceLiveStatusIntervalId);
  }, [shootInstances]);

  useEffect(() => {
    if (params.gearId) {
      setTimeout(() => {
        Object.keys(shootRobots).forEach((robotId, index) => {
          const robotData = shootRobots[robotId];
          if (params.gearId === robotData?.air_id) {
            setSelectedRobotId(robotId);
            return;
          }
        });
      }, 500);
    }
  }, [params]);

  return (
    <LaunchPadLayout links={links}>
      <MainContentContainer
        ref={ref}
        onChange={() => console.log('Changed!')}
      >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: stageRef.current?.width(),
              height: stageRef.current?.height(),
            }}
          >
            <Stage
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
              }}
              draggable={selectedRobotId ? false : true}
              ref={stageRef}
              x={position.x}
              y={position.y}
              onWheel={handleWheel}
              scaleX={scale}
              scaleY={scale}
              onTouchMove={handleMultiTouch}
              onTouchEnd={multiTouchEnd}
              onDragStart={handleDragStart}
            >
              <Layer>
                <Image
                  x={0}
                  y={0}
                  image={image}
                  width={image?.width}
                  height={image?.height}
                />
              </Layer>
              <Layer>
                <>
                  {Object.keys(shootRobots).map((robotId, i) => {
                    let selectedShootRobot = shootRobots[robotId];
                    if (selectedShootRobot.is_in_canvas){
                      let operatorData = getRobotOperatorData(robotId);
                      let robotStatusObj = robotLiveStatus?.[selectedShootRobot.robot_id];
                      return (
                        <Portal
                          selector=".focused"
                          key={robotId}
                          enabled={robotId.toString() === selectedRobotId?.toString() ? true : false}
                        >
                          <RobotCanvasItem
                            robotData={selectedShootRobot}
                            operatorData={operatorData}
                            scale={scale}
                            zoomScale={state.shoot?.scale}
                            memberItemMoving={false}
                            robotStatusObj={robotStatusObj}
                            userId={userInformation?.data?.user}
                            userRole={currentUserRole()}
                            draggable={false}
                            onDblClick={onDblClick}
                          />
                        </Portal>
                      );
                    }
                  })}
                </>
              </Layer>
              {selectedRobotId ? (
                <Layer
                  width={stageRef.current?.width()}
                  height={stageRef.current?.height()}
                >
                  <Rect
                    x={-position.x / scale}
                    y={-position.y / scale}
                    width={stageRef.current?.width() / scale}
                    height={stageRef.current?.height() / scale}
                    fill="#000000"
                    opacity={0.4}
                    onClick={handleFocusOut}
                    onTouchEnd={handleFocusOut}
                  />
                </Layer>
              ) : null}
              <Layer name="focused" />
            </Stage>
          </div>

          <InstanceLauncherPopup
            instance={selectedInstance}
            instanceNickName={selectedInstanceNickName}
            orgName={orgName}
            ownerName={ownerName}
            ownerEmail={ownerEmail}
            curUserId={curUserId}
            curUserName={curUserName}
            curUserEmail={curUserEmail}
            projectName={projectName}
            onHide={() => {
              setShowInstanceLaunchModal(false);
              setSelectedInstance({});
              setSelectedInstanceNickName('');
            }}
            showInstanceLaunchModal={showInstanceLaunchModal}
            closeInstanceLaunchModal={() => {
              setShowInstanceLaunchModal(false);
              setSelectedInstance({});
              setSelectedInstanceNickName('');
            }}
          />

          <BottomUsersNavContainer2>
            <BottomInstanceNav
              instances={shootInstances}
              onlineMembers={userInformation?.onlineMembers}
              onDblClick={onDblClick}
              onClick={onClickInstance}
              shootInstanceLiveStatuses={gear.shootInstanceLiveStatuses}
            />
            <BottomUsersNav
              shoot={state?.shoot}
              permission_obj={state?.shoot?.permission_obj}
              onlineMembers={userInformation?.onlineMembers}
            />
          </BottomUsersNavContainer2>

          <ZoomToolContainer>
            <ZoomTool
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              centered={handleCentered}
            />
          </ZoomToolContainer>
      </MainContentContainer>
    </LaunchPadLayout>
  );
};

export default Main;
